import { z } from "zod"
import { branchIdSchema, correlationIdSchema, entityIdSchema, legalEntityIdSchema } from "./identifiers.js"

const code = z.string().trim().min(1).max(140)
export const inventoryMasterKindV1Schema = z.enum(["item", "supplier", "category", "unit"])
export const inventoryMasterAdminV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
  masterId: entityIdSchema,
  revisionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  masterKind: inventoryMasterKindV1Schema,
  code,
  displayName: z.string().trim().min(1).max(240),
  status: z.enum(["active", "inactive"]),
  effectiveFrom: z.string().date(),
  details: z.record(z.string(), z.union([z.string(), z.boolean()])),
}).strict().superRefine((value, context) => {
  const keys = new Set(Object.keys(value.details))
  const expected = value.masterKind === "item"
    ? ["categoryCode", "stockUom", "isStockItem", "maintainStock"]
    : value.masterKind === "supplier"
      ? ["supplierGroup", "supplierType", "defaultCurrency"]
      : value.masterKind === "unit" ? ["wholeNumberOnly"] : []
  if (keys.size !== expected.length || expected.some((key) => !keys.has(key))) {
    context.addIssue({ code: "custom", message: "Master details do not match masterKind." })
  }
  if (value.masterKind === "item" && value.details.maintainStock === true && value.details.isStockItem !== true) {
    context.addIssue({ code: "custom", message: "Stock maintenance requires a stock Item." })
  }
  if (value.masterKind === "supplier" && !["company", "individual"].includes(String(value.details.supplierType))) {
    context.addIssue({ code: "custom", message: "Supplier type is invalid." })
  }
})

export const purchasingPolicyPublishV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
  policyId: entityIdSchema,
  revisionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  effectiveFrom: z.string().date(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  manualRequestLimitMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  approvalRequiredAboveMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  budgetContextRequired: z.boolean(),
  requesterMayApprove: z.boolean(),
  allowReorderSuggestions: z.boolean(),
}).strict().refine((value) => value.approvalRequiredAboveMinor <= value.manualRequestLimitMinor, {
  message: "Approval threshold cannot exceed the manual request limit.",
})

export const inventoryMasterResultV1Schema = z.object({
  entityType: z.enum(["inventory_master", "purchasing_policy"]),
  entityId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  state: z.enum(["active", "inactive", "published"]),
  version: z.number().int().positive(),
  providerDoctype: z.string().min(1).max(140),
  providerName: z.string().min(1).max(240),
  usableBy: z.array(z.enum(["catalog", "visit", "accounting", "procurement"])).min(1),
}).strict()

export type InventoryMasterAdminV1 = z.infer<typeof inventoryMasterAdminV1Schema>
export type PurchasingPolicyPublishV1 = z.infer<typeof purchasingPolicyPublishV1Schema>
